import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';
import { incrementLocalSearchCalls } from '../utils/benchmarkInstrumentation';

/**
 * Standard SFLA + GA baseline hybrid.
 *
 * This baseline keeps the core SFLA memeplex search and augments it with
 * ordinary GA crossover and mutation inside each memeplex. It is intentionally
 * conservative so it can serve as the scientifically clean comparator for the
 * proposed SFLAGA variant.
 */

function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  const memeplexCount = memeplexes.length;

  for (let i = 0; i < population.length; i++) {
    const memeplexIdx = i % memeplexCount;
    const frogIdx = Math.floor(i / memeplexCount);

    if (frogIdx < memeplexes[memeplexIdx].length) {
      population[i] = memeplexes[memeplexIdx][frogIdx];
    }
  }
}

function createRandomChromosome(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  const chromosome: Chromosome = {
    x: Array(taskCount)
      .fill(0)
      .map(() => rng.randomInt(serviceCount)),
    fitness: 0,
    rwp: 0,
  };

  calculateChromosomeFitness(chromosome, matrix, taskCount, serviceCount);
  return chromosome;
}

function updateWorstFrog(
  worst: Chromosome,
  guide: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  incrementLocalSearchCalls();
  const maxStep = Math.max(1, Math.min(5, Math.floor(taskCount / 10)));
  const candidate: Chromosome = {
    x: [...worst.x],
    fitness: 0,
    rwp: 0,
  };

  const copiedIndices = new Set<number>();
  const step = rng.randomIntRange(1, maxStep);
  for (let i = 0; i < step && copiedIndices.size < taskCount; i++) {
    let taskIdx: number;
    do {
      taskIdx = rng.randomInt(taskCount);
    } while (copiedIndices.has(taskIdx));

    copiedIndices.add(taskIdx);
    candidate.x[taskIdx] = guide.x[taskIdx];
  }

  calculateChromosomeFitness(candidate, matrix, taskCount, serviceCount);
  return candidate;
}

function selectParent(
  memeplex: Chromosome[],
  selectionRate: number,
  rng: SeededRNG
): Chromosome {
  if (rng.randomChance(selectionRate / 100)) {
    return memeplex[tournamentSelection(memeplex, rng)];
  }

  return memeplex[rng.randomInt(memeplex.length)];
}

function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  rng: SeededRNG
): void {
  if (memeplex.length === 0) return;

  memeplex.sort((a, b) => b.fitness - a.fitness);
  const eliteCount = Math.max(1, Math.floor(memeplex.length * 0.1));
  const localBest = memeplex[0];
  const worstIdx = memeplex.length - 1;
  const worst = memeplex[worstIdx];

  let improved = updateWorstFrog(
    worst,
    localBest,
    taskCount,
    serviceCount,
    matrix,
    rng
  );

  if (improved.fitness > worst.fitness) {
    memeplex[worstIdx] = improved;
  } else {
    improved = updateWorstFrog(
      worst,
      globalBest,
      taskCount,
      serviceCount,
      matrix,
      rng
    );

    memeplex[worstIdx] =
      improved.fitness > worst.fitness
        ? improved
        : createRandomChromosome(taskCount, serviceCount, matrix, rng);
  }

  memeplex.sort((a, b) => b.fitness - a.fitness);

  const children: Chromosome[] = [];
  const numPairs = Math.max(1, Math.floor((memeplex.length - eliteCount) / 2));

  for (let i = 0; i < numPairs; i++) {
    if (!rng.randomChance(crossoverRate / 100)) {
      continue;
    }

    const parent1 = selectParent(memeplex, selectionRate, rng);
    let parent2 = selectParent(memeplex, selectionRate, rng);

    let retries = 0;
    while (parent2 === parent1 && memeplex.length > 1 && retries < 10) {
      parent2 = selectParent(memeplex, selectionRate, rng);
      retries++;
    }

    let [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);

    if (rng.randomChance(mutationRate / 100)) {
      child1 = mutate(child1, taskCount, serviceCount, rng);
    }
    if (rng.randomChance(mutationRate / 100)) {
      child2 = mutate(child2, taskCount, serviceCount, rng);
    }

    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);
    children.push(child1, child2);
  }

  children.sort((a, b) => b.fitness - a.fitness);

  let replaceIdx = memeplex.length - 1;
  for (const child of children) {
    while (replaceIdx >= eliteCount) {
      if (child.fitness > memeplex[replaceIdx].fitness) {
        memeplex[replaceIdx] = child;
        replaceIdx--;
        break;
      }
      replaceIdx--;
    }

    if (replaceIdx < eliteCount) {
      break;
    }
  }

  memeplex.sort((a, b) => b.fitness - a.fitness);
}

export async function runSFLAGABaseline(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
  }
): Promise<Chromosome> {
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;

  const population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );

  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  population.sort((a, b) => b.fitness - a.fitness);
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100;

  for (let gen = 0; gen < parameters.generations; gen++) {
    const globalBest = findBestChromosome(population);

    for (const memeplex of memeplexes) {
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        parameters.selectionRate,
        parameters.crossoverRate,
        parameters.mutationRate,
        rng
      );
    }

    shuffleMemeplexes(memeplexes, population);
    population.sort((a, b) => b.fitness - a.fitness);
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      now - lastUpdateTime > UPDATE_INTERVAL ||
      gen % 10 === 9 ||
      gen === parameters.generations - 1;

    if (shouldUpdate) {
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, population[0].fitness);
      }
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  return findBestChromosome(population);
}
